import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface ImageFullscreenData {
  url: string;
  filename?: string;
}

@Component({
  selector: 'app-image-fullscreen-viewer',
  templateUrl: './image-fullscreen-viewer.component.html',
  styleUrls: ['./image-fullscreen-viewer.component.scss'],
})
export class ImageFullscreenViewerComponent {
  url: string;
  filename: string;

  scale = 1;
  translateX = 0;
  translateY = 0;
  isDragging = false;

  readonly minScale = 1;
  readonly maxScale = 5;
  readonly zoomStep = 0.5;

  private dragStartX = 0;
  private dragStartY = 0;
  private startTranslateX = 0;
  private startTranslateY = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ImageFullscreenData,
    private dialogRef: MatDialogRef<ImageFullscreenViewerComponent>,
  ) {
    this.url = data.url;
    this.filename = data.filename ?? '';
  }

  get transform(): string {
    return `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
  }

  zoomIn(): void {
    this.setScale(this.scale + this.zoomStep);
  }

  zoomOut(): void {
    this.setScale(this.scale - this.zoomStep);
  }

  reset(): void {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
  }

  onWheel(event: WheelEvent): void {
    event.preventDefault();
    const delta = event.deltaY < 0 ? this.zoomStep : -this.zoomStep;
    this.setScale(this.scale + delta);
  }

  onDoubleClick(): void {
    if (this.scale > 1) {
      this.reset();
    } else {
      this.setScale(2.5);
    }
  }

  onPointerDown(event: PointerEvent): void {
    if (this.scale <= 1) return;
    this.isDragging = true;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.startTranslateX = this.translateX;
    this.startTranslateY = this.translateY;
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.isDragging) return;
    this.translateX = this.startTranslateX + (event.clientX - this.dragStartX);
    this.translateY = this.startTranslateY + (event.clientY - this.dragStartY);
  }

  onPointerUp(): void {
    this.isDragging = false;
  }

  close(): void {
    this.dialogRef.close();
  }

  /** Close only when the dark area (not the image or the controls) is clicked. */
  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('viewer')) {
      this.close();
    }
  }

  private setScale(next: number): void {
    const clamped = Math.min(this.maxScale, Math.max(this.minScale, +next.toFixed(2)));
    this.scale = clamped;
    if (clamped === this.minScale) {
      this.translateX = 0;
      this.translateY = 0;
    }
  }
}
